/* @jsx h */
import { defineComponent, h } from "vue";
import type { PromotionReward, PromotionRequirement } from "@lemmonhq/types";
import {
  ProductRewardEditor,
  ProductCategoryRewardEditor,
  ProductDiscountRewardEditor,
  OrderDiscountRewardEditor,
  SpecialPriceRewardEditor,
} from "../subpages/Promotions/components/index";

/**
 * "What does this promotion give?" — the type selector plus the matching
 * variant editor.
 *
 * Extracted from NewPromotionEditor so redemption tiers on a coin program edit
 * their reward with exactly the same control. A tier is an ordinary promotion,
 * and two divergent reward editors would be two sets of defaults to keep in
 * step.
 */

/** A sensible starting value per variant, so switching type never yields a half-filled reward. */
export const defaultRewardForType = (
  type: PromotionReward["type"],
): PromotionReward => {
  switch (type) {
    case "product":
      return { type: "product", quantity: 1 };
    case "product_category":
      return { type: "product_category", categories: [], quantity: 1 };
    case "product_discount":
      return { type: "product_discount", mode: "percentage", value: 10 };
    case "order_discount":
      return { type: "order_discount", mode: "percentage", value: 10 };
    case "special_price":
      return { type: "special_price", prices: {} };
    default:
      return { type: "product_discount", mode: "percentage", value: 10 };
  }
};

export type PromotionRewardEditorProps = {
  value: PromotionReward;
  onChange: (reward: PromotionReward) => void;
  /** `special_price` reads these to price the required products. */
  requirements?: PromotionRequirement[];
  /** Heading above the selector. Omit for a bare control. */
  title?: string;
  /**
   * Free-product rewards are hidden in the promotions editor but are the whole
   * point of a coin tier ("10 stamps → a free coffee"), so it is opt-in.
   */
  allowProductReward?: boolean;
  /** Limit variants when the owner scope cannot resolve product identities. */
  allowedRewardTypes?: PromotionReward["type"][];
};

export const PromotionRewardEditor =
  defineComponent<PromotionRewardEditorProps>({
    name: "PromotionRewardEditor",
    props: [
      "value",
      "onChange",
      "requirements",
      "title",
      "allowProductReward",
      "allowedRewardTypes",
    ],
    setup(props) {
      const isAllowed = (type: PromotionReward["type"]) =>
        !props.allowedRewardTypes || props.allowedRewardTypes.includes(type);
      const changeType = (type: PromotionReward["type"]) =>
        props.onChange(defaultRewardForType(type));

      const renderVariant = () => {
        switch (props.value.type) {
          case "product":
            return (
              <ProductRewardEditor
                {...{ value: props.value, onChange: props.onChange }}
              />
            );
          case "product_category":
            return (
              <ProductCategoryRewardEditor
                {...{ value: props.value, onChange: props.onChange }}
              />
            );
          case "product_discount":
            return (
              <ProductDiscountRewardEditor
                {...{ value: props.value, onChange: props.onChange }}
              />
            );
          case "order_discount":
            return (
              <OrderDiscountRewardEditor
                {...{ value: props.value, onChange: props.onChange }}
              />
            );
          case "special_price":
            return (
              <SpecialPriceRewardEditor
                {...{
                  value: props.value,
                  onChange: props.onChange,
                  requirements: props.requirements ?? [],
                }}
              />
            );
          default:
            return null;
        }
      };

      return () => (
        <div class="card">
          <div class="card-body p-0">
            <div class="flex items-center justify-between mb-4 gap-3">
              {props.title ? (
                <h3 class="card-title text-xl">{props.title}</h3>
              ) : (
                <span />
              )}
              <select
                value={props.value.type}
                onChange={(e) =>
                  changeType(
                    (e.target as HTMLSelectElement)
                      .value as PromotionReward["type"],
                  )
                }
                class="select select-bordered select focus:select-primary"
              >
                {props.allowProductReward && isAllowed("product") && (
                  <option value="product">🎁 Free Product</option>
                )}
                {props.allowProductReward && isAllowed("product_category") && (
                  <option value="product_category">
                    🎁 Free Product from Categories
                  </option>
                )}
                {isAllowed("product_discount") && (
                  <option value="product_discount">🏷️ Product Discount</option>
                )}
                {isAllowed("order_discount") && (
                  <option value="order_discount">🛒 Order Discount</option>
                )}
                {isAllowed("special_price") && (
                  <option value="special_price">💰 Special Price</option>
                )}
              </select>
            </div>

            {renderVariant()}
          </div>
        </div>
      );
    },
  });
